// Builds a React Flow graph for the scenario architecture.
// Produces one sceneNode per scene (column header) + one scenarioElementNode
// per element inside each scene, with edges from element nodes to the target
// scene node for every navigate action.
//
// Terminal scenes (no outgoing navigate edge from any element) are connected
// to a synthetic End node so every possible flow path terminates explicitly.

const SCENE_W = 200;
const SCENE_H = 62;
const SCENE_BOTTOM_PAD = 12;
const EL_H = 42;
const EL_GAP = 6;
const COL_GAP = 110;
const COL_START_Y = 40;
const COL_START_X = 40;
const END_PADDING = 80;

export function buildScenarioGraph(scenario) {
  const scenes = (scenario && scenario.scenes) || [];
  const startSceneId = scenario && scenario.startSceneId;

  const nodes = [];
  const edges = [];
  const seen = new Set();
  // Tracks which scene IDs have at least one element with an outgoing navigate action.
  const scenesWithNavigate = new Set();

  let globalMaxY = COL_START_Y;

  for (let i = 0; i < scenes.length; i++) {
    const scene = scenes[i];
    const x = COL_START_X + i * (SCENE_W + COL_GAP);
    let y = COL_START_Y;

    // Scene header node
    nodes.push({
      id: `sc:${scene.id}`,
      type: "sceneNode",
      position: { x, y },
      data: {
        sceneId: scene.id,
        title: scene.title || "Sahne",
        elementCount: (scene.elements || []).length,
        isStart: scene.id === startSceneId,
        bgColor: scene.bgColor || "#1a1a2e",
      },
    });

    y += SCENE_H + SCENE_BOTTOM_PAD;

    // Element nodes — stacked below the scene header
    for (const el of scene.elements || []) {
      nodes.push({
        id: `se:${scene.id}:${el.id}`,
        type: "scenarioElementNode",
        position: { x: x + 10, y },
        data: {
          elementId: el.id,
          sceneId: scene.id,
          elType: el.type,
          title: el.title || el.type,
        },
      });
      y += EL_H + EL_GAP;

      // Emit edges for navigate actions
      const emitEdge = (actions, label) => {
        for (const a of actions || []) {
          if (a.type !== "navigate" || !a.targetSceneId) continue;
          const key = `${el.id}→${a.targetSceneId}`;
          if (seen.has(key)) continue;
          seen.add(key);
          scenesWithNavigate.add(scene.id);
          edges.push({
            id: `sce-${el.id}-${a.targetSceneId}`,
            source: `se:${scene.id}:${el.id}`,
            target: `sc:${a.targetSceneId}`,
            type: "default",
            label: label || undefined,
            animated: false,
            style: { stroke: "rgba(139,92,246,0.65)", strokeWidth: 1.5 },
            markerEnd: { type: "arrowclosed", color: "rgba(139,92,246,0.65)" },
          });
        }
      };

      switch (el.type) {
        case "visualtext":
          for (const btn of el.buttons || []) emitEdge(btn.actions, btn.label);
          break;
        case "flipcard":
          emitEdge(el.backActions, el.backButtonLabel || "Arka");
          break;
        case "truefalse":
          emitEdge(el.correctActions, el.trueLabel || "Doğru");
          emitEdge(el.incorrectActions, el.falseLabel || "Yanlış");
          break;
        case "mcq":
          for (const c of el.choices || []) emitEdge(c.actions, c.text || "Seçenek");
          break;
        default:
          break;
      }
    }

    if (y > globalMaxY) globalMaxY = y;
  }

  // End node + terminal scene edges — every flow path must reach End.
  if (scenes.length > 0) {
    const endY = globalMaxY + END_PADDING;
    // Center End horizontally across all scene columns.
    const endX = COL_START_X + Math.round(((scenes.length - 1) * (SCENE_W + COL_GAP)) / 2);

    nodes.push({
      id: "synthetic:scenario-end",
      type: "endNode",
      data: { label: "End", satisfied: true },
      position: { x: endX, y: endY },
      draggable: false,
      selectable: false,
    });

    // Terminal scenes: those where no element emitted a navigate edge.
    const terminalSceneIds = scenes.filter((s) => !scenesWithNavigate.has(s.id)).map((s) => s.id);

    terminalSceneIds.forEach((sceneId) => {
      edges.push({
        id: `e:scenario-end:${sceneId}`,
        source: `sc:${sceneId}`,
        target: "synthetic:scenario-end",
        targetHandle: "in",
        label: "end",
        animated: false,
      });
    });
  }

  return { nodes, edges };
}
